// @intent: PixiJS runtime host for renderer-neutral render models
// @flow: RuntimeCanvas compiles model and loads assets -> PixiRuntimeCanvas mounts Application and adapter renderers
// @stale: update when Pixi Application setup or viewport transform semantics change

import React from 'react';
import { Application, extend } from '@pixi/react';
import { Container, Graphics, Sprite, Text as PixiText } from 'pixi.js';
import type {
  JsonObject,
  Match3NativeRuntimePayloadV1,
  PlayableAPI,
  PlayableLayerSetProps,
  PlayablePointerEvent,
  PlayablePointerEventType,
  PlayablePointerHandler,
  Project,
  RenderModel,
} from '@mintyads/playable-api';
import { validateMatch3NativeRuntimePayloadV1 } from '@mintyads/playable-api';
import { createViewportTransform, type RuntimeDriveInputPayload, type RuntimeInputPayload, type RuntimeRect } from '@mintyads/shared-types';
import { PixiHighlightOverlay } from './PixiHighlightOverlay';
import { PixiLayerRenderer } from './PixiLayerRenderer';
import { applyPixiLayerSetProps, createPixiPlayableApi } from './pixiPlayableApi';
import { createRuntimeCleanupScope } from './runtimeCleanup';
import { buildMatch3Snapshot, chooseMostSpecificMatch3Rect, driveInputPointerSequence, isMatch3BoardLayerId, isMatch3CtaLayerId, type Match3Hooks, type Match3SnapshotPayload, type RuntimeMatch3Controls } from './match3DriveCapture';
import { Match3NativeRenderer } from './match3/Match3NativeRenderer';
import { toExecutableGeneratedCode } from '../generatedCode';
import { collectRuntimeProbeSnapshot } from '../probe/runtimeProbeCollector';
import {
  createRuntimeProbeInputBuffer,
  designPointToClient,
  probeInputFromRuntimeEvidence,
  rawProbeInputFromClientPointer,
  type RuntimeProbeHitTargets,
} from '../probe/runtimeProbeInput';
import type { RuntimeProbeAction, RuntimeProbeActionRecord, RuntimeProbeControls, RuntimeProbeTransform } from '../probe/runtimeProbeTypes';
import { createRuntimeProbeTweenRegistry } from '../probe/runtimeProbeTweens';

extend({ Container, Graphics, Sprite, Text: PixiText });

type GeneratedSetup = (api: PlayableAPI) => unknown;

function isGeneratedSetup(value: unknown): value is GeneratedSetup {
  return typeof value === 'function';
}

function isCleanupFunction(value: unknown): value is () => void {
  return typeof value === 'function';
}

function setPointerCaptureIfAvailable(target: EventTarget, pointerId: number): void {
  const candidate = target as { readonly setPointerCapture?: (pointerId: number) => void };
  candidate.setPointerCapture?.(pointerId);
}

function releasePointerCaptureIfAvailable(target: EventTarget, pointerId: number): void {
  const candidate = target as {
    readonly hasPointerCapture?: (pointerId: number) => boolean;
    readonly releasePointerCapture?: (pointerId: number) => void;
  };
  if (candidate.hasPointerCapture?.(pointerId) === true) {
    candidate.releasePointerCapture?.(pointerId);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stableJsonKey(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function readDisplayParent(target: Record<string, unknown>): Record<string, unknown> | null {
  const parent = target.parent;
  return parent !== null && typeof parent === 'object' && !Array.isArray(parent) ? parent as Record<string, unknown> : null;
}

function isEffectivelyVisible(target: Record<string, unknown>): boolean {
  let current: Record<string, unknown> | null = target;
  let depth = 0;
  while (current && depth < 64) {
    if (current.visible === false || current.renderable === false) return false;
    current = readDisplayParent(current);
    depth += 1;
  }
  return true;
}

interface Match3AcceptedMoveMarker {
  readonly isAvailable: boolean;
  readonly key: string | null;
}

function readMatch3AcceptedMoveMarker(providers: Map<string, () => unknown>): Match3AcceptedMoveMarker {
  const provider = providers.get('match3');
  if (!provider) return { isAvailable: false, key: null };
  const state = provider();
  if (!isRecord(state)) return { isAvailable: false, key: null };
  if (typeof state.acceptedMoveSeq === 'number' && Number.isFinite(state.acceptedMoveSeq)) {
    return { isAvailable: true, key: `seq:${String(state.acceptedMoveSeq)}` };
  }
  if (Object.prototype.hasOwnProperty.call(state, 'lastAcceptedMove')) {
    const move = state.lastAcceptedMove;
    return { isAvailable: true, key: move === null || move === undefined ? null : stableJsonKey(move) };
  }
  return { isAvailable: false, key: null };
}

function isInputProbeAction(kind: RuntimeProbeAction['kind']): boolean {
  return kind === 'tap' || kind === 'swipe' || kind === 'drag';
}

function toJsonRecord(value: unknown): Record<string, never> {
  return JSON.parse(JSON.stringify(value)) as Record<string, never>;
}

export function shouldMountNativeMatch3Renderer(project: Project): boolean {
  const parsed = validateMatch3NativeRuntimePayloadV1(project.variables.nativeMatch3);
  return parsed.ok;
}

function readNativeMatch3Payload(project: Project): Match3NativeRuntimePayloadV1 | null {
  if (!shouldMountNativeMatch3Renderer(project)) return null;
  const parsed = validateMatch3NativeRuntimePayloadV1(project.variables.nativeMatch3);
  return parsed.ok ? parsed.value : null;
}

function isNativeMatch3DynamicLayerId(id: string): boolean {
  return /^match-tile-\d+$/.test(id) || /^match-static-\d+$/.test(id);
}

// @intent Layer IDs owned by Match3NativeRenderer that must be suppressed from the scene-graph so the
//         renderer-drawn versions win (background cover-fit; board panel programmatic graphic; HUD pill; title).
// @flow filterNativeMatch3RenderRoot removes these IDs; Match3NativeRenderer renders their equivalents.
// @stale 2026-06-10 — native-match3-background added (Fix 2: cover-fit replaces scene-graph contain/letterbox);
//         native-match3-board-panel added (Fix 1: programmatic graphic replaces baked-grid AI image);
//         native-match3-hud added (Fix 3: programmatic pill replaces stretched hudPanel image).
// @stale 2026-06-11 — native-match3-title added (P16c Fix 3): scene-graph text was at y:39 (under phone notch);
//         renderer now draws the title at hud.y+hud.height/2 (below SAFE_AREA_TOP).
// @stale 2026-06-11 — native-match3-endcard + native-match3-endcard-backdrop added (Fix 1): renderer owns
//         endcard visibility gating (visible={ended}); any scene-graph copies must be suppressed so they
//         cannot show the endcard chrome during pre-terminal gameplay.
const NATIVE_MATCH3_OWNED_LAYER_IDS = new Set([
  'native-match3-background',
  'native-match3-board-panel',
  'native-match3-hud',
  'native-match3-title',
  'native-match3-endcard',
  'native-match3-endcard-backdrop',
]);

function isNativeMatch3OwnedLayerId(id: string): boolean {
  return isNativeMatch3DynamicLayerId(id) || NATIVE_MATCH3_OWNED_LAYER_IDS.has(id);
}

export function filterNativeMatch3RenderRoot(root: RenderModel['root']): RenderModel['root'] {
  const filterNode = (node: RenderModel['root']): RenderModel['root'] | null => {
    if (isNativeMatch3OwnedLayerId(node.id)) return null;
    const rawChildren = (node as { readonly children?: unknown }).children;
    if (!Array.isArray(rawChildren)) return node;
    const children = rawChildren
      .map((child) => filterNode(child as RenderModel['root']))
      .filter((child): child is RenderModel['root'] => child !== null);
    return { ...node, children };
  };
  return filterNode(root) ?? root;
}

export interface PixiRuntimeCanvasProps {
  readonly renderModel: RenderModel;
  readonly project: Project;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly highlightedLayerIds: readonly string[];
  readonly assetBaseUrl?: string | undefined;
  readonly onLayerSelected: (layerId: string | null) => void;
  readonly onRuntimeError: (code: string, message: string) => void;
  readonly onRuntimeAnalytics?: ((name: string, data?: JsonObject) => void) | undefined;
  readonly onRuntimeInput?: ((payload: RuntimeInputPayload) => void) | undefined;
  readonly onRuntimeControls?: ((controls: RuntimeMatch3Controls | null) => void) | undefined;
  readonly onRuntimeProbeControls?: ((controls: RuntimeProbeControls | null) => void) | undefined;
}

export function PixiRuntimeCanvas({
  renderModel,
  project,
  viewport,
  highlightedLayerIds,
  assetBaseUrl,
  onLayerSelected,
  onRuntimeError,
  onRuntimeAnalytics,
  onRuntimeInput,
  onRuntimeControls,
  onRuntimeProbeControls,
}: PixiRuntimeCanvasProps) {
  const transform = React.useMemo(
    () => createViewportTransform(renderModel.viewport, viewport),
    [renderModel.viewport, viewport],
  );
  const targetRegistryRef = React.useRef(new Map<string, Record<string, unknown>>());
  const pendingLayerPropsRef = React.useRef(new Map<string, PlayableLayerSetProps>());
  const inputHandlersRef = React.useRef(new Map<PlayablePointerEventType, Set<PlayablePointerHandler>>());
  const [inputActive, setInputActive] = React.useState(false);
  const inputLayerRef = React.useRef<HTMLDivElement>(null);
  const probeFrameIndexRef = React.useRef(0);
  const probeInputBufferRef = React.useRef(createRuntimeProbeInputBuffer());
  const probeTweenRegistryRef = React.useRef(createRuntimeProbeTweenRegistry());
  const probeStateProvidersRef = React.useRef(new Map<string, () => unknown>());
  const nativeMatch3Payload = React.useMemo(() => readNativeMatch3Payload(project), [project]);
  const renderRoot = React.useMemo(
    () => nativeMatch3Payload ? filterNativeMatch3RenderRoot(renderModel.root) : renderModel.root,
    [nativeMatch3Payload, renderModel.root],
  );

  const createProbeTransform = React.useCallback((): RuntimeProbeTransform => {
    const layerRect = inputLayerRef.current?.getBoundingClientRect();
    return {
      designViewport: renderModel.viewport,
      runtimeViewport: viewport,
      canvasClientRect: layerRect
        ? { x: layerRect.left, y: layerRect.top, width: layerRect.width, height: layerRect.height }
        : { x: 0, y: 0, width: viewport.width, height: viewport.height },
      scaleX: transform.scaleX,
      scaleY: transform.scaleY,
      offsetX: transform.offsetX,
      offsetY: transform.offsetY,
      devicePixelRatio: window.devicePixelRatio,
    };
  }, [renderModel.viewport, transform, viewport]);

  const collectProbeHitTargets = React.useCallback((): RuntimeProbeHitTargets => {
    const layerRect = inputLayerRef.current?.getBoundingClientRect();
    if (!layerRect) return new Map();
    const targets = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const [id, node] of targetRegistryRef.current.entries()) {
      const n = node as { getBounds?: () => unknown };
      if (!isEffectivelyVisible(node) || typeof n.getBounds !== 'function') continue;
      try {
        const raw = n.getBounds() as { x?: number; y?: number; width?: number; height?: number; rectangle?: { x: number; y: number; width: number; height: number } };
        const r = raw.rectangle ?? raw;
        if (typeof r.x !== 'number' || typeof r.y !== 'number' || typeof r.width !== 'number' || typeof r.height !== 'number') continue;
        if (!Number.isFinite(r.x) || !Number.isFinite(r.y) || !Number.isFinite(r.width) || !Number.isFinite(r.height)) continue;
        targets.set(id, { x: layerRect.left + r.x, y: layerRect.top + r.y, width: r.width, height: r.height });
      } catch {
        // Hit target collection is diagnostic-only; malformed targets are reported by the snapshot collector.
      }
    }
    return targets;
  }, []);

  const registerTarget = React.useCallback((layerId: string, target: Record<string, unknown>) => {
    targetRegistryRef.current.set(layerId, target);
    const pendingProps = pendingLayerPropsRef.current.get(layerId);
    if (pendingProps) {
      applyPixiLayerSetProps(target, pendingProps);
      pendingLayerPropsRef.current.delete(layerId);
    }
    return () => {
      if (targetRegistryRef.current.get(layerId) === target) {
        targetRegistryRef.current.delete(layerId);
      }
    };
  }, []);

  const onInput = React.useCallback((type: PlayablePointerEventType, handler: PlayablePointerHandler) => {
    let handlers = inputHandlersRef.current.get(type);
    if (!handlers) {
      handlers = new Set<PlayablePointerHandler>();
      inputHandlersRef.current.set(type, handlers);
    }
    handlers.add(handler);
    setInputActive(true);
    return () => {
      const currentHandlers = inputHandlersRef.current.get(type);
      currentHandlers?.delete(handler);
      if (currentHandlers?.size === 0) inputHandlersRef.current.delete(type);
      setInputActive(inputHandlersRef.current.size > 0);
    };
  }, []);

  React.useEffect(() => {
    if (nativeMatch3Payload) return undefined;
    if (!project.code) return undefined;

    let cancelled = false;
    let cleanup: (() => void) | undefined;
    const cleanupScope = createRuntimeCleanupScope();
    probeTweenRegistryRef.current.clear();
    probeStateProvidersRef.current.clear();
    const moduleUrl = URL.createObjectURL(new Blob([toExecutableGeneratedCode(project.code)], { type: 'text/javascript' }));
    const api = createPixiPlayableApi({
      project,
      renderModel,
      viewport,
      mode: 'edit',
      emit: (name, data) => { onRuntimeAnalytics?.(name, data); },
      targetRegistry: targetRegistryRef.current,
      pendingLayerProps: pendingLayerPropsRef.current,
      assetBaseUrl,
      onInput,
      onInputEvidence: (payload) => {
        probeInputBufferRef.current.push(probeInputFromRuntimeEvidence({
          payload,
          transform: createProbeTransform(),
          hitTargets: collectProbeHitTargets(),
        }));
        onRuntimeInput?.(payload);
      },
      cleanupScope,
      probeTweenRegistry: probeTweenRegistryRef.current,
      probeStateProviders: probeStateProvidersRef.current,
    });

    void import(/* @vite-ignore */ moduleUrl)
      .then((module: unknown) => {
        if (cancelled) return;
        const setup = (module as { readonly setup?: unknown }).setup;
        if (!isGeneratedSetup(setup)) {
          throw new Error('generated code module does not export setup(api)');
        }
        const result: unknown = setup(api);
        if (isCleanupFunction(result)) cleanup = result;
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          onRuntimeError('generated-code-failed', error instanceof Error ? error.message : String(error));
        }
      });

    return () => {
      cancelled = true;
      try {
        cleanup?.();
      } catch (error) {
        onRuntimeError('generated-code-cleanup-failed', error instanceof Error ? error.message : String(error));
      } finally {
        try {
          cleanupScope.dispose();
        } catch (error) {
          onRuntimeError('generated-runtime-cleanup-failed', error instanceof Error ? error.message : String(error));
        }
        URL.revokeObjectURL(moduleUrl);
      }
    };
  }, [assetBaseUrl, collectProbeHitTargets, createProbeTransform, nativeMatch3Payload, onInput, onRuntimeAnalytics, onRuntimeError, onRuntimeInput, project, renderModel, viewport]);

  const dispatchInput = React.useCallback((
    type: PlayablePointerEventType,
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    const handlers = inputHandlersRef.current.get(type);
    if (!handlers || handlers.size === 0) return;

    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const playableEvent: PlayablePointerEvent = {
      x: (event.clientX - bounds.left - transform.offsetX) / transform.scaleX,
      y: (event.clientY - bounds.top - transform.offsetY) / transform.scaleY,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      isPrimary: event.isPrimary,
    };
    probeInputBufferRef.current.push(rawProbeInputFromClientPointer({
      type,
      phase: 'raw',
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      transform: createProbeTransform(),
      hitTargets: collectProbeHitTargets(),
    }));
    for (const handler of [...handlers]) handler(playableEvent);
  }, [collectProbeHitTargets, createProbeTransform, transform]);

  // §10 verifier controls. driveInput dispatches REAL PointerEvents on the .runtime-input-layer (the SAME path real input
  // takes via dispatchInput — never a direct game-handler call). getMatch3Snapshot pairs the live window.__match3 logical
  // state with the rendered Pixi client-px bounds (board/CTA) so the verifier can prove visual presence AND catch
  // "logical changed but visuals didn't".
  const driveInput = React.useCallback((payload: RuntimeDriveInputPayload) => {
    const layer = inputLayerRef.current;
    if (!layer) return;
    const rect = layer.getBoundingClientRect();
    for (const ev of driveInputPointerSequence(payload, transform, rect.left, rect.top)) {
      layer.dispatchEvent(new PointerEvent(ev.type, {
        clientX: ev.clientX,
        clientY: ev.clientY,
        pointerId: 1,
        isPrimary: true,
        pointerType: 'touch',
        bubbles: true,
        cancelable: true,
      }));
    }
  }, [transform]);

  const driveProbeAction = React.useCallback((action: RuntimeProbeAction): RuntimeProbeActionRecord => {
    const startedAtMs = Date.now();
    const finish = (
      result: RuntimeProbeActionRecord['result'],
      summary: string,
    ): RuntimeProbeActionRecord => ({
      id: `probe-action-${String(startedAtMs)}`,
      kind: action.kind,
      startedAtMs,
      finishedAtMs: Date.now(),
      input: toJsonRecord(action),
      result,
      summary,
    });

    const layer = inputLayerRef.current;
    if (!layer) return finish('error', 'runtime input layer unavailable');

    if (action.kind === 'wait') return finish('accepted', `wait requested for ${String(action.durationMs)}ms`);
    if (action.kind === 'query') return finish('accepted', `query requested: ${action.label}`);
    if (action.kind === 'resize') return finish('rejected', 'resize must be applied by the parent harness viewport');
    if (inputHandlersRef.current.size === 0) return finish('rejected', 'no runtime input handlers registered');

    const probeTransform = createProbeTransform();
    const rect = layer.getBoundingClientRect();
    const toClient = (point: { readonly coordinateSpace: 'design' | 'client'; readonly x: number; readonly y: number }) => {
      if (point.coordinateSpace === 'client') {
        return { clientX: rect.left + point.x, clientY: rect.top + point.y };
      }
      return designPointToClient(point, probeTransform);
    };
    const dispatchPointer = (type: 'pointerdown' | 'pointermove' | 'pointerup', clientX: number, clientY: number) => {
      layer.dispatchEvent(new PointerEvent(type, {
        clientX,
        clientY,
        pointerId: 1,
        isPrimary: true,
        pointerType: 'touch',
        bubbles: true,
        cancelable: true,
      }));
    };
    const beforeInputCount = probeInputBufferRef.current.snapshot().length;
    const beforeMatch3Move = readMatch3AcceptedMoveMarker(probeStateProvidersRef.current);

    if (action.kind === 'tap') {
      const client = toClient(action.at);
      dispatchPointer('pointerdown', client.clientX, client.clientY);
      dispatchPointer('pointerup', client.clientX, client.clientY);
    } else {
      const from = toClient(action.from);
      const to = toClient(action.to);
      const mid = {
        clientX: (from.clientX + to.clientX) / 2,
        clientY: (from.clientY + to.clientY) / 2,
      };
      dispatchPointer('pointerdown', from.clientX, from.clientY);
      dispatchPointer('pointermove', mid.clientX, mid.clientY);
      dispatchPointer('pointermove', to.clientX, to.clientY);
      dispatchPointer('pointerup', to.clientX, to.clientY);
    }

    const afterInput = probeInputBufferRef.current.snapshot();
    const afterMatch3Move = readMatch3AcceptedMoveMarker(probeStateProvidersRef.current);
    let accepted = afterInput.slice(beforeInputCount).some((event) => event.phase === 'accepted');
    let summary = `${action.kind} dispatched`;
    if (isInputProbeAction(action.kind) && (beforeMatch3Move.isAvailable || afterMatch3Move.isAvailable)) {
      accepted = afterMatch3Move.key !== null && afterMatch3Move.key !== beforeMatch3Move.key;
      summary = accepted
        ? `${action.kind} dispatched and accepted by match3 state`
        : `${action.kind} dispatched without match3 state acceptance`;
    }
    return finish(accepted ? 'accepted' : 'rejected', summary);
  }, [createProbeTransform]);

  const clientRectOf = React.useCallback((predicate: (id: string) => boolean): RuntimeRect | null => {
    try {
      const layerRect = inputLayerRef.current?.getBoundingClientRect();
      if (!layerRect) return null;
      const candidates: RuntimeRect[] = [];
      for (const [id, node] of targetRegistryRef.current.entries()) {
        if (!predicate(id)) continue;
        const n = node as { getBounds?: () => unknown };
        if (!isEffectivelyVisible(node)) continue;
        if (typeof n.getBounds !== 'function') return null;
        const raw = n.getBounds() as { x?: number; y?: number; width?: number; height?: number; rectangle?: { x: number; y: number; width: number; height: number } };
        const r = raw.rectangle ?? raw;
        if (typeof r.x !== 'number' || typeof r.y !== 'number' || typeof r.width !== 'number' || typeof r.height !== 'number') return null;
        candidates.push({ x: layerRect.left + r.x, y: layerRect.top + r.y, width: r.width, height: r.height });
      }
      return chooseMostSpecificMatch3Rect(candidates);
    } catch {
      return null;
    }
  }, []);
  const getMatch3Snapshot = React.useCallback((): Match3SnapshotPayload => {
    const hooks = (globalThis as { __match3?: Match3Hooks }).__match3;
    return buildMatch3Snapshot(
      hooks,
      clientRectOf(isMatch3BoardLayerId),
      clientRectOf(isMatch3CtaLayerId),
      clientRectOf((id) => id === 'tutorial-hand'),
    );
  }, [clientRectOf]);
  React.useEffect(() => {
    if (!onRuntimeControls) return undefined;
    onRuntimeControls({ driveInput, getMatch3Snapshot });
    return () => { onRuntimeControls(null); };
  }, [onRuntimeControls, driveInput, getMatch3Snapshot]);

  const getProbeSnapshot = React.useCallback((label: string) => {
    return collectRuntimeProbeSnapshot({
      projectId: project.id,
      runtimeVersion: 'r8',
      label,
      frameIndex: probeFrameIndexRef.current++,
      transform: createProbeTransform(),
      targetRegistry: targetRegistryRef.current,
      recentInput: probeInputBufferRef.current.snapshot(),
      activeTweens: probeTweenRegistryRef.current.snapshot(),
      stateProviders: probeStateProvidersRef.current,
    });
  }, [createProbeTransform, project.id]);

  React.useEffect(() => {
    if (!onRuntimeProbeControls) return undefined;
    onRuntimeProbeControls({ getProbeSnapshot, driveProbeAction });
    return () => { onRuntimeProbeControls(null); };
  }, [driveProbeAction, getProbeSnapshot, onRuntimeProbeControls]);

  return (
    <div
      className="pixi-runtime-surface"
      style={{ position: 'relative', width: viewport.width, height: viewport.height }}
    >
      <Application
        width={viewport.width}
        height={viewport.height}
        backgroundAlpha={0}
        autoDensity
        resolution={window.devicePixelRatio}
      >
        <pixiContainer
          x={transform.offsetX}
          y={transform.offsetY}
          scale={{ x: transform.scaleX, y: transform.scaleY }}
        >
          <PixiLayerRenderer
            node={renderRoot}
            onLayerSelected={onLayerSelected}
            registerTarget={registerTarget}
          />
          {nativeMatch3Payload ? (
            <Match3NativeRenderer
              payload={nativeMatch3Payload}
              designViewport={renderModel.viewport}
              designTitle={project.manifest.name}
              registerTarget={registerTarget}
              onLayerSelected={onLayerSelected}
              onInput={onInput}
              emit={(name, data) => { onRuntimeAnalytics?.(name, data); }}
              probeStateProviders={probeStateProvidersRef.current}
              probeTweenRegistry={probeTweenRegistryRef.current}
            />
          ) : null}
          <PixiHighlightOverlay root={renderRoot} highlightedLayerIds={highlightedLayerIds} />
        </pixiContainer>
      </Application>
      <div
        ref={inputLayerRef}
        aria-hidden="true"
        className="runtime-input-layer"
        style={{
          position: 'absolute',
          inset: 0,
          touchAction: 'none',
          pointerEvents: inputActive ? 'auto' : 'none',
        }}
        onPointerDown={(event) => {
          setPointerCaptureIfAvailable(event.currentTarget, event.pointerId);
          dispatchInput('pointerdown', event);
        }}
        onPointerMove={(event) => { dispatchInput('pointermove', event); }}
        onPointerUp={(event) => {
          dispatchInput('pointerup', event);
          releasePointerCaptureIfAvailable(event.currentTarget, event.pointerId);
        }}
        onPointerCancel={(event) => { dispatchInput('pointercancel', event); }}
      />
    </div>
  );
}
